import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotificationsDialog from "@/components/workspace/dialogs/NotificationsDialog";
import { WorkspaceThreadsProvider } from "@/contexts/WorkspaceThreadsContext";
import { useProjectThreads } from "@/features/sidebar/projects/hooks/useProjectThreads";
import { useThreadNewTracking } from "@/features/sidebar/projects/hooks/useThreadNewTracking";
import { isDefaultTitle } from "@/features/sidebar/projects/utils";
import { useThreadHistory } from "@/hooks";
import { useNewChat } from "@/hooks/useNewChat";
import { cn } from "@/lib/utils";
import { TEST_IDS } from "@/constants/testIds";
import { useAssistantStore, useAuthStore } from "@/store";
import { useThreadStore } from "@/store/threadStore";
import { useTheme } from "@/contexts/ThemeProvider";
import {
  ChevronsUpDown,
  FileText,
  Images,
  Laptop,
  Loader2,
  LogIn,
  Moon,
  PanelLeft,
  PenSquare,
  SearchIcon,
  Sun,
} from "lucide-react";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { SearchDialog } from "../dialogs";
import { NavHistory } from "./nav-history";
import { NavProjects } from "./nav-projects";
import { NavUser } from "./nav-user";
import { OrgSwitcher } from "./OrgSwitcher";

/**
 * GuestFooter - Footer for unauthenticated users with sign-in button and theme toggle
 * Matches the NavUser visual pattern
 */
function GuestFooter() {
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();

  const themeIcon =
    theme === "dark" ? (
      <Moon className="h-4 w-4" />
    ) : theme === "light" ? (
      <Sun className="h-4 w-4" />
    ) : (
      <Laptop className="h-4 w-4" />
    );

  const themeLabel =
    theme === "dark" ? "Dark" : theme === "light" ? "Light" : "System";

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground group-data-[collapsible=icon]:justify-center"
            >
              <div className="h-8 w-8 shrink-0 rounded-full bg-gradient-to-r from-purple-500/20 to-blue-500/20 flex items-center justify-center">
                <LogIn className="h-4 w-4 text-purple-500" />
              </div>
              <div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
                <span className="truncate font-semibold">Guest</span>
                <span className="truncate text-xs text-sidebar-foreground/50">
                  Sign in for full access
                </span>
              </div>
              <ChevronsUpDown className="ml-auto size-4 group-data-[collapsible=icon]:hidden" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            side="bottom"
            align="end"
            sideOffset={4}
          >
            <DropdownMenuItem
              onClick={() => navigate("/auth/login")}
              className="cursor-pointer"
            >
              <LogIn className="mr-2 h-4 w-4" />
              Sign In
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() =>
                setTheme(
                  theme === "dark"
                    ? "light"
                    : theme === "light"
                      ? "system"
                      : "dark"
                )
              }
              onSelect={(e) => e.preventDefault()}
              className="cursor-pointer"
            >
              {themeIcon}
              <span className="ml-2">Theme: {themeLabel}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

/**
 * AppSidebar - Complete sidebar implementation using sidebar-07 pattern
 *
 * Features:
 * - Collapsible sidebar with icon mode support
 * - All navigation components (Projects, Chats, User)
 * - Show/hide functionality with SidebarRail
 * - Search and new chat functionality
 * - Notifications integration
 * - Responsive design with mobile support
 */
export const AppSidebar = React.memo(
  function AppSidebar({ tempChat = false, ...props }) {
    const [isSearchOpen, setIsSearchOpen] = React.useState(false);
    const [searchQuery, setSearchQuery] = React.useState("");
    const { threads, setFilteredThreads } = useThreadStore();
    const { handleThreadSelect } = useThreadHistory();
    const navigate = useNavigate();
    const { toggleSidebar } = useSidebar();
    const [isHoveringToggle, setIsHoveringToggle] = React.useState(false);

    // Auth store for user info and workspaces
    const { user, currentWorkspace, workspaces } = useAuthStore();

    // Thread new tracking for animations
    const { markThreadAsNew } = useThreadNewTracking();

    // Workspace threads state - lifted here for centralized drag-and-drop handling
    const { workspaceThreads, setWorkspaceThreads, isLoadingThreads } =
      useProjectThreads({
        workspaces,
        isDefaultTitle,
        markThreadAsNew,
      });
    const { currentAssistant, createTemporaryDefaultAssistant } =
      useAssistantStore();

    // Notifications state
    const [isNotificationsOpen, setIsNotificationsOpen] = React.useState(false);

    // Use centralized new chat hook
    const { handleNewChat, isCreatingChat } = useNewChat();

    // Platform detection for shortcuts
    const isMac =
      typeof window !== "undefined" &&
      navigator.platform.toUpperCase().indexOf("MAC") >= 0;
    const modifierKey = isMac ? "⌘" : "Ctrl";

    // Enhanced new chat handler that creates assistant if needed
    const handleNewChatWithAssistant = React.useCallback(async () => {
      if (!currentAssistant) {
        try {
          // Try to create a temporary assistant if none exists
          const userDisplayName =
            user?.user_metadata?.full_name ||
            user?.email?.split("@")[0] ||
            "User";
          await createTemporaryDefaultAssistant(userDisplayName);
        } catch (error) {
          console.error("Failed to create temporary assistant:", error);
          // Still allow navigation - user can create assistant manually
        }
      }
      handleNewChat();
    }, [
      currentAssistant,
      user,
      createTemporaryDefaultAssistant,
      handleNewChat,
    ]);

    // Handle opening the search dialog
    const handleSearchToggle = React.useCallback(() => {
      setIsSearchOpen(true);
    }, []);

    // Add keyboard shortcuts for New Chat and Search
    React.useEffect(() => {
      const handleKeyDown = (event) => {
        // Cmd/Ctrl + M for New Chat
        if (
          (event.metaKey || event.ctrlKey) &&
          event.key.toLowerCase() === "m"
        ) {
          event.preventDefault();
          handleNewChatWithAssistant();
        }
        // Cmd/Ctrl + K for Search
        if (
          (event.metaKey || event.ctrlKey) &&
          event.key.toLowerCase() === "k"
        ) {
          event.preventDefault();
          handleSearchToggle();
        }
      };

      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }, [handleNewChatWithAssistant, handleSearchToggle]);

    return (
      <TooltipProvider>
        <Sidebar
          collapsible="icon"
          variant="inset"
          role="navigation"
          aria-label="Main navigation"
          className={cn(
            "h-full flex-shrink-0 flex flex-col",
            "overflow-hidden",
            "z-[300]",
            "bg-sidebar"
          )}
          data-testid={TEST_IDS.SIDEBAR}
          {...props}
        >
          <SidebarHeader
            className={cn(
              "w-full max-w-full min-w-0 overflow-hidden",
              "bg-sidebar",
              "pb-0",
              "group-data-[collapsible=icon]:h-0 group-data-[collapsible=icon]:p-0 group-data-[collapsible=icon]:overflow-hidden"
            )}
          >
            <div className="flex items-center justify-between w-full">
              {/* Logo and Text - Left Side */}
              <a
                href={tempChat ? "/" : "/chat"}
                className={cn(
                  "flex items-center gap-2 hover:opacity-80 transition-opacity",
                  "group-data-[collapsible=icon]:hidden",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400 focus-visible:ring-offset-2 focus-visible:rounded-md focus-visible:relative focus-visible:z-[100]"
                )}
              >
                <img
                  src="/assets/artifact.webp"
                  alt="Artifact Logo"
                  className="h-8 w-8 aspect-square flex-shrink-0"
                />
                <span className="text-sm font-semibold text-sidebar-foreground">
                  Artifact
                </span>
              </a>

              {/* Toggle Sidebar Button - Right Side (only visible when expanded) */}
              <div className="flex-shrink-0 group-data-[collapsible=icon]:hidden">
                <button
                  onClick={toggleSidebar}
                  className={cn(
                    "flex items-center justify-center h-8 w-8 rounded-full",
                    "bg-transparent hover:bg-sidebar-accent",
                    "transition-colors duration-200",
                    "text-sidebar-foreground/90 hover:text-sidebar-foreground",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400 focus-visible:ring-offset-2 focus-visible:relative focus-visible:z-[100]"
                  )}
                  data-testid={TEST_IDS.SIDEBAR_TOGGLE}
                >
                  <PanelLeft className="h-4 w-4" />
                  <span className="sr-only">Toggle Sidebar</span>
                </button>
              </div>
            </div>
          </SidebarHeader>

          {/* Org switcher — only renders when user belongs to orgs */}
          {user && <OrgSwitcher />}

          <SidebarContent
            className={cn(
              "flex flex-col flex-1 overflow-hidden p-0",
              "w-full max-w-full min-w-0",
              "bg-sidebar"
            )}
          >
            {tempChat ? (
              <>
                {/* Logo Toggle Button - Only visible when collapsed */}
                <SidebarGroup className="w-full min-w-0 max-w-full flex-shrink-0 group-data-[collapsible=icon]:flex hidden">
                  <SidebarMenu className="w-full min-w-0 max-w-full">
                    <SidebarMenuItem className="w-full min-w-0 max-w-full">
                      <SidebarMenuButton
                        onClick={toggleSidebar}
                        tooltip={`Toggle Sidebar (${modifierKey}B)`}
                        className={cn(
                          "w-full min-w-0 max-w-full",
                          "transition-none overflow-visible",
                          "focus-visible:ring-2 focus-visible:ring-purple-400 focus-visible:ring-offset-2 focus-visible:relative focus-visible:z-[100]",
                          !isHoveringToggle &&
                            "group-data-[collapsible=icon]:!p-0"
                        )}
                        onMouseEnter={() => setIsHoveringToggle(true)}
                        onMouseLeave={() => setIsHoveringToggle(false)}
                      >
                        {!isHoveringToggle ? (
                          <img
                            src="/assets/artifact.webp"
                            alt="Artifact Logo"
                            className="h-8 w-8 aspect-square flex-shrink-0"
                          />
                        ) : (
                          <PanelLeft className="h-4 w-4 flex-shrink-0" />
                        )}
                        <span className="sr-only">Toggle Sidebar</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  </SidebarMenu>
                </SidebarGroup>
                {/* Sign in prompt - hidden when collapsed */}
                <div className="flex-1 flex items-center justify-center p-3 bg-sidebar group-data-[collapsible=icon]:hidden">
                  <div className="text-center">
                    <div className="mb-4">
                      <div className="w-12 h-12 mx-auto rounded-full bg-gradient-to-r from-purple-500/20 to-blue-500/20 flex items-center justify-center mb-3">
                        <svg
                          className="w-6 h-6 text-purple-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                          />
                        </svg>
                      </div>
                      <p className="text-sm text-sidebar-foreground/70 mb-2">
                        Sign in to access
                      </p>
                      <p className="text-xs text-sidebar-foreground/50">
                        Chat history & projects
                      </p>
                    </div>
                    <button
                      onClick={() => navigate("/auth/login")}
                      className="w-full py-2 px-4 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-md text-sm font-medium hover:from-purple-600 hover:to-blue-600 transition-all duration-200"
                    >
                      Sign In
                    </button>
                  </div>
                </div>
              </>
            ) : user ? (
              <>
                {/* Fixed section: New Chat, Search, Library, and Documents */}
                <SidebarGroup className="w-full min-w-0 max-w-full flex-shrink-0">
                  <SidebarMenu className="w-full min-w-0 max-w-full">
                    {/* Logo Toggle Button - Only visible when collapsed */}
                    <SidebarMenuItem className="w-full min-w-0 max-w-full group-data-[collapsible=icon]:flex hidden">
                      <SidebarMenuButton
                        onClick={toggleSidebar}
                        tooltip={`Toggle Sidebar (${modifierKey}B)`}
                        className={cn(
                          "w-full min-w-0 max-w-full",
                          "transition-none overflow-visible",
                          "focus-visible:ring-2 focus-visible:ring-purple-400 focus-visible:ring-offset-2 focus-visible:relative focus-visible:z-[100]",
                          !isHoveringToggle &&
                            "group-data-[collapsible=icon]:!p-0"
                        )}
                        onMouseEnter={() => setIsHoveringToggle(true)}
                        onMouseLeave={() => setIsHoveringToggle(false)}
                      >
                        {!isHoveringToggle ? (
                          <img
                            src="/assets/artifact.webp"
                            alt="Artifact Logo"
                            className="h-8 w-8 aspect-square flex-shrink-0"
                          />
                        ) : (
                          <PanelLeft className="h-4 w-4 flex-shrink-0" />
                        )}
                        <span className="sr-only">Toggle Sidebar</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    <SidebarMenuItem className="w-full min-w-0 max-w-full">
                      <SidebarMenuButton
                        onClick={handleNewChatWithAssistant}
                        tooltip={
                          isCreatingChat
                            ? "Creating new chat..."
                            : !currentWorkspace
                              ? "Select a workspace first"
                              : `Start new chat (${modifierKey}M)`
                        }
                        disabled={isCreatingChat || !currentWorkspace}
                        className={cn(
                          "w-full min-w-0 max-w-full",
                          (isCreatingChat || !currentWorkspace) &&
                            "opacity-50 cursor-not-allowed"
                        )}
                        data-testid={TEST_IDS.SIDEBAR_NEW_CHAT_BUTTON}
                      >
                        {isCreatingChat ? (
                          <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin" />
                        ) : (
                          <PenSquare className="h-4 w-4 flex-shrink-0" />
                        )}
                        <span className="truncate min-w-0 group-data-[collapsible=icon]:hidden">
                          New Chat
                        </span>
                        <span className="ml-auto text-[10px] text-sidebar-foreground/40 font-medium group-data-[collapsible=icon]:hidden opacity-0 group-hover/menu-item:opacity-100 transition-opacity">
                          {modifierKey}M
                        </span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    <SidebarMenuItem className="w-full min-w-0 max-w-full">
                      <SidebarMenuButton
                        onClick={handleSearchToggle}
                        tooltip={`Search chats (${modifierKey}K)`}
                        className="w-full min-w-0 max-w-full"
                        data-testid={TEST_IDS.SIDEBAR_SEARCH_BUTTON}
                      >
                        <SearchIcon className="h-4 w-4 flex-shrink-0" />
                        <span className="truncate min-w-0 group-data-[collapsible=icon]:hidden">
                          Search
                        </span>
                        <span className="ml-auto text-[10px] text-sidebar-foreground/40 font-medium group-data-[collapsible=icon]:hidden opacity-0 group-hover/menu-item:opacity-100 transition-opacity">
                          {modifierKey}K
                        </span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    <SidebarMenuItem className="w-full min-w-0 max-w-full">
                      <SidebarMenuButton
                        asChild
                        isActive={
                          typeof window !== "undefined" &&
                          window.location.pathname === "/library"
                        }
                        tooltip="Library"
                        className="w-full min-w-0 max-w-full"
                      >
                        <button
                          onClick={() => navigate("/library")}
                          className="w-full min-w-0 max-w-full flex items-center gap-2"
                          data-testid={TEST_IDS.SIDEBAR_LIBRARY_BUTTON}
                        >
                          <Images className="h-4 w-4 flex-shrink-0" />
                          <span className="truncate min-w-0 group-data-[collapsible=icon]:hidden">
                            Library
                          </span>
                        </button>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    <SidebarMenuItem className="w-full min-w-0 max-w-full">
                      <SidebarMenuButton
                        asChild
                        isActive={
                          typeof window !== "undefined" &&
                          window.location.pathname === "/documents"
                        }
                        tooltip="Documents"
                        className="w-full min-w-0 max-w-full"
                      >
                        <button
                          onClick={() => navigate("/documents")}
                          className="w-full min-w-0 max-w-full flex items-center gap-2"
                          data-testid={TEST_IDS.SIDEBAR_DOCUMENTS_BUTTON}
                        >
                          <FileText className="h-4 w-4 flex-shrink-0" />
                          <span className="truncate min-w-0 group-data-[collapsible=icon]:hidden">
                            Documents
                          </span>
                        </button>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  </SidebarMenu>
                </SidebarGroup>

                {/* Scrollable section: Projects and Chats */}
                <ScrollArea
                  className={cn(
                    "flex-1 w-full max-w-full min-w-0 overflow-hidden",
                    "bg-sidebar",
                    // Custom scrollbar positioning - move to left
                    "[&>[data-radix-scroll-area-viewport]]:!pr-0",
                    "[&>[data-radix-scroll-area-scrollbar]]:!left-0 [&>[data-radix-scroll-area-scrollbar]]:!right-auto",
                    // Custom scrollbar styling - theme aware
                    "[&>[data-radix-scroll-area-scrollbar]]:bg-sidebar-accent",
                    "[&>[data-radix-scroll-area-scrollbar]>[data-radix-scroll-area-thumb]]:bg-sidebar-accent-foreground/20",
                    "[&>[data-radix-scroll-area-scrollbar]>[data-radix-scroll-area-thumb]]:hover:bg-sidebar-accent-foreground/30",
                    "[&>[data-radix-scroll-area-scrollbar]]:w-2"
                  )}
                >
                  <WorkspaceThreadsProvider
                    workspaceThreads={workspaceThreads}
                    setWorkspaceThreads={setWorkspaceThreads}
                    isLoadingThreads={isLoadingThreads}
                  >
                    <NavProjects />
                    <NavHistory searchQuery={searchQuery} />
                  </WorkspaceThreadsProvider>
                </ScrollArea>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center p-3 bg-sidebar">
                <div className="text-center">
                  <div className="mb-4">
                    <div className="w-12 h-12 mx-auto rounded-full bg-gradient-to-r from-purple-500/20 to-blue-500/20 flex items-center justify-center mb-3">
                      <svg
                        className="w-6 h-6 text-purple-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                        />
                      </svg>
                    </div>
                    <p className="text-sm text-sidebar-foreground/70 mb-2">
                      Sign in to access
                    </p>
                    <p className="text-xs text-sidebar-foreground/50">
                      Chat history & projects
                    </p>
                  </div>
                  <button
                    onClick={() => navigate("/auth/login")}
                    className="w-full py-2 px-4 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-md text-sm font-medium hover:from-purple-600 hover:to-blue-600 transition-all duration-200"
                  >
                    Sign In
                  </button>
                </div>
              </div>
            )}
          </SidebarContent>

          <SidebarFooter
            className={cn(
              "pb-0 w-full max-w-full min-w-0 overflow-hidden",
              "bg-sidebar border-t border-sidebar-border",
              "group-data-[collapsible=icon]:pb-1"
            )}
          >
            {user ? (
              <NavUser
                notificationsDialogOpen={isNotificationsOpen}
                setNotificationsDialogOpen={setIsNotificationsOpen}
                tempChat={tempChat}
              />
            ) : (
              <GuestFooter />
            )}
          </SidebarFooter>

          {/* SidebarRail for show/hide functionality */}
          <SidebarRail />

          {/* Search Dialog */}
          <SearchDialog
            isOpen={isSearchOpen}
            setIsOpen={setIsSearchOpen}
            handleThreadSelect={handleThreadSelect}
            handleNewChat={handleNewChat}
          />

          {/* Notifications Dialog - using workspace notifications with better styling */}
          <NotificationsDialog
            isOpen={isNotificationsOpen}
            onOpenChange={setIsNotificationsOpen}
          />
        </Sidebar>
      </TooltipProvider>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison function - always return true to prevent rerenders
    // The component will only rerender if its internal state changes
    return true;
  }
);
